import React, { useState, useCallback, ReactElement } from "react"
import { qual } from "app/static/frontend/QuorumAlert"

/**
 * Determines what state the upload is in, which determines what message is shown to the user
 */
type UploadStatus = "NONE" | "SUCCESS" | "UPLOADING" | "ERROR"

/**
 * Represents the result of a submitted insert image form
 */
export interface InsertImageFormResult {
    /** URL of the image to be inserted */
    imageUrl: string
    /** What url the link wrapping the image should have, or empty if image should not be a link  */
    imageLink: string
}

/**
 * Helper that deals with uploading an image. If the image uploads successfully this will return the S3 url,
 * otherwise the promise will raise an error.
 *
 * @param file - The file to be uploaded, found at input.files[0] usually
 * @returns - Promise resolving the uploaded url if successful
 */
const uploadImage = async (file: File): Promise<string> => {
    const data = new FormData()
    data.append("file", file)
    const response = await window.fetch("/outbox/upload_image/", {
        method: "POST",
        cache: "no-cache",
        mode: "same-origin",
        credentials: "include",
        headers: new Headers({ "X-CSRFToken": Userdata.csrftoken }),
        body: data,
    })
    if (!response.ok) {
        throw new Error("Image upload response is not ok")
    }

    const responseUrl = await response.text()

    return Promise.resolve(responseUrl)
}

/**
 * The Insert Image form, which gets rendered inside a QuorumAlert. Using openSummernoteImageLinkDialog will ensure
 * this gets used properly.
 *
 * This form will call onChange({ imageUrl, imageLink }) on input changes.
 */
const SummernoteInsertImageForm = ({ onChange }: { onChange: (res: InsertImageFormResult) => void }): ReactElement => {
    const [imageUrl, setImageUrl] = useState("")
    const [imageLink, setImageLink] = useState("")
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>("NONE")

    const handleUploadOnChange = useCallback(
        async (evt) => {
            const file = evt.target.files[0]
            if (!file || uploadStatus === "UPLOADING") {
                return
            }

            setUploadStatus("UPLOADING")

            try {
                const uploadUrl = await uploadImage(file)
                setImageUrl(uploadUrl)
                setUploadStatus("SUCCESS")
                onChange({ imageUrl: uploadUrl, imageLink })
            } catch (err) {
                console.warn("Image Upload Failed. Error output below.")
                console.error(err)

                setUploadStatus("ERROR")
            }
        },
        [imageLink, uploadStatus, onChange]
    )

    const handleImageUrlOnChange = useCallback(
        (evt) => {
            setImageUrl(evt.target.value)
            setUploadStatus("NONE")
            onChange({ imageLink, imageUrl: evt.target.value })
        },
        [imageLink, onChange]
    )

    const handleImageLinkOnChange = useCallback(
        (evt) => {
            setImageLink(evt.target.value)
            onChange({ imageLink: evt.target.value, imageUrl })
        },
        [imageUrl, setImageLink, onChange]
    )

    return (
        <div>
            <div className="form-group note-form-group note-form-group-select-from-files">
                <label className="note-form-label" htmlFor="summernote-link-image-upload">
                    Select from files
                </label>
                <input
                    id="summernote-link-image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    className="note-image-input form-control-file note-form-control note-input"
                    onChange={handleUploadOnChange}
                    disabled={uploadStatus === "UPLOADING"}
                />
                {uploadStatus === "UPLOADING" && <span>Uploading...</span>}
                {uploadStatus === "SUCCESS" && <span>Successfully uploaded image.</span>}
                {uploadStatus === "ERROR" && <span>Image Upload Failed. Try selecting the image again.</span>}
            </div>
            <div className="form-group note-group-image-url">
                <label className="note-form-label" htmlFor="summernote-link-image-url">
                    Image URL
                </label>
                <input
                    id="summernote-link-image-url"
                    type="text"
                    className="note-image-url form-control note-form-control note-input"
                    value={imageUrl}
                    onChange={handleImageUrlOnChange}
                />
            </div>
            <div className="form-group">
                <label className="note-form-label" htmlFor="summernote-link-url">
                    Link to URL (optional)
                </label>
                <input
                    id="summernote-link-url"
                    type="text"
                    className="form-control note-form-control"
                    value={imageLink}
                    onChange={handleImageLinkOnChange}
                />
            </div>
        </div>
    )
}

/**
 * Opens a QuorumAlert dialog prompting user for an image URL and optional link, allowing them to upload their own
 * image.
 *
 * Returns a promise that resolves to an object { imageUrl, imageLink } with the entered data, or undefined if the
 * user cancels or doesn't interact with the dialog.
 */
export const openSummernoteImageLinkDialog = async (): Promise<InsertImageFormResult | undefined> => {
    let result = undefined

    const dialogResult = await qual.fire({
        title: "Insert Image",
        children: (
            <SummernoteInsertImageForm
                onChange={(newResult) => {
                    result = newResult
                }}
            />
        ),
        actions: {
            submit: "Submit",
            cancel: "Cancel",
        },
    })

    return Promise.resolve(dialogResult.value === "submit" ? result : undefined)
}
